/**
 * The pack opening, in three dimensions.
 *
 * Everything before this faked light with CSS gradients over a photograph, and
 * every attempt to break the pack up looked like a sliced photograph, because
 * that is what it was. Here the pack is an object: it is lit, it has thickness,
 * and the pieces it breaks into were cut in Blender and have real interior
 * faces. The glow is emissive geometry through a bloom pass rather than a
 * radial gradient pretending to be one.
 *
 * The GLB holds the intact pack and every chunk at the same origin, so the
 * burst is a swap rather than a transition: hide the pack, show the pieces,
 * throw them. Its base colour map is replaced per set. The geometry is the
 * same pouch whatever is printed on it.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import {
  EffectComposer, EffectPass, RenderPass, SelectiveBloomEffect, SMAAEffect, SMAAPreset,
} from 'postprocessing';
import { HalfFloatType } from 'three';

import { getEffectivePixelRatio } from '../graphicsSettings';

/** The pack is modelled at life size in metres; frame it like a held object. */
const CAMERA_FOV = 34;
const CAMERA_DISTANCE = 0.52;

/**
 * The pack is framed small on purpose.
 *
 * Its print comes from a catalogue render, and after cropping to the face the
 * panels are 662, 324 and 230 pixels wide. Filling the screen with that is a
 * five- to sevenfold upscale, which is the whole reason it reads as an old
 * low-detail render however good the lighting is. Held at roughly half the
 * viewport it lands near 1:1 and looks like the crisp artwork it came from.
 */

/**
 * Ceiling on the render scale for this scene.
 *
 * `getEffectivePixelRatio` goes to 2.25 on a Retina panel at the Ultra preset,
 * and this composer carries an HDR half-float target plus a six-level bloom
 * chain plus SMAA. Every one of which is allocated and shaded at that scale,
 * now across the whole screen rather than a small box. Bloom is a soft effect
 * with no fine detail to lose, so it is capped here: the difference is not
 * visible and the saving is most of the cost.
 */
const MAX_PIXEL_RATIO = 1.35;

/** How far the pack leans at the edge of the canvas, in radians. */
const MAX_YAW = 0.42;
const MAX_PITCH = 0.26;
/** Time constant of the lean. This is what gives it mass. */
const LEAN_TAU = 0.16;

const CHARGE_S = 0.62;
/**
 * When the scene is told it is spent and the caller takes it away.
 *
 * Long, because this is not how long the burst lasts. It is how long the
 * wreckage stays on screen behind the reveal. The card fan alone runs about a
 * second, and cutting the debris before it finishes is what made the opening
 * look like it stopped and handed over to an unrelated screen.
 */
const BURST_S = 2.2;
/** How long after the burst the bloom pass is still worth paying for. */
const BLOOM_UNTIL_S = 0.55;
/** How far the pack rises when the player commits, in metres. */
const LIFT = 0.018;

const hash = (i) => {
  const s = Math.sin(i * 12.9898) * 43758.5453;
  return s - Math.floor(s);
};

export class PackScene {
  /** @param {HTMLCanvasElement} canvas */
  constructor(canvas) {
    this.canvas = canvas;
    this.time = 0;
    this.phase = 'idle';
    this.phaseT = 0;
    this.lean = { x: 0, y: 0 };
    this.leanTarget = { x: 0, y: 0 };
    this.chunks = [];
    this.crackMaterials = [];
    this.intact = null;
    this.ready = false;

    this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: false });
    this.renderer.setPixelRatio(Math.min(MAX_PIXEL_RATIO, getEffectivePixelRatio()));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.95;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(CAMERA_FOV, 1, 0.01, 5);
    this.camera.position.set(0, 0, CAMERA_DISTANCE);

    // The foil needs a world to mirror. Same approach as the dice tray.
    this.pmrem = new THREE.PMREMGenerator(this.renderer);
    const room = new RoomEnvironment();
    this.envRT = this.pmrem.fromScene(room, 0.04);
    room.dispose();
    this.scene.environment = this.envRT.texture;
    // **The print is already lit.**
    //
    // Its artwork is a painted catalogue image with its own light baked in, and
    // most of it is near-black. Lighting it a second time at the intensities a
    // bare object would need washed it out: the environment reflects off the
    // whole surface at once, and a broad specular sheen lifts black artwork to
    // grey. Which reads as an overexposed bloom sitting over the pack. Diffuse
    // light cannot do that (black albedo stays black however bright the lamp),
    // so the sheen is what came down hardest. The key stays strong enough to
    // give the pouch its form.
    this.scene.environmentIntensity = 0.12;

    const key = new THREE.DirectionalLight(0xfff2dc, 1.35);
    key.position.set(-0.25, 0.3, 0.4);
    this.scene.add(key);
    const rim = new THREE.DirectionalLight(0x9fc4ff, 0.45);
    rim.position.set(0.3, -0.1, -0.35);
    this.scene.add(rim);
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.12));

    // The light inside the pack, as a billboard with a soft falloff rather
    // than a sphere. Geometry has a silhouette: however bright and however
    // additive, a sphere is a disc with an edge, and an edge is the one thing
    // light does not have. The bloom then spreads what this puts down.
    // **No aura billboard.**
    //
    // Three attempts at one. A gradient sprite, noise-displaced, then polar
    // filaments. And every version still read as a circle stuck behind the
    // pack, because that is what a camera-facing quad is. The light in this
    // effect has a shape already: the seams the pack splits along. Those are
    // emissive and they bloom, so the glow comes out of the pack's own
    // geometry instead of being a disc parked behind it.
    this.coreLight = new THREE.PointLight(0xffe6b0, 0, 0.5, 2);

    this.pack = new THREE.Group();
    this.scene.add(this.pack);
    // The light is *inside* the pack, so it travels with it. Left at the scene
    // origin it stayed put while the pack rose on the charge, and the glow
    // ended up hanging below the pack instead of shining out of it.
    this.pack.add(this.coreLight);

    this.composer = new EffectComposer(this.renderer, { frameBufferType: HalfFloatType });
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    // **Selective, and the selection is the light alone.**
    //
    // A whole-scene bloom was wrong in a way tuning cannot fix. The pack's own
    // print goes well past any threshold low enough to catch the burst, so the
    // pack bloomed. A bright rectangle smeared across six mip levels, which
    // is a large soft grey rectangle sitting behind the pack, and the middle of
    // the artwork blown to white. The glow belongs to the light source, not to
    // everything the light falls on. Same reason `tableScene` runs its bloom
    // inverted to keep foils out of it.
    this.bloom = new SelectiveBloomEffect(this.scene, this.camera, {
      mipmapBlur: true,
      intensity: 1.3,
      luminanceThreshold: 0.1,
      luminanceSmoothing: 0.1,
      radius: 0.72,
      levels: 5,
    });
    // The cracks are the light source now, so they are what blooms.
    this.bloomSelection = this.bloom.selection;
    this.composer.addPass(new EffectPass(
      this.camera, this.bloom, new SMAAEffect({ preset: SMAAPreset.MEDIUM }),
    ));

    // Baking the mirror world draws a white room; clear it before anyone sees.
    this.renderer.setRenderTarget(null);
    this.renderer.clear();
  }

  /**
   * Load the pack and dress it in this set's print.
   *
   * @param glbUrl    the shared pack + chunk geometry
   * @param panelUrl  `booster-<set>-panel.webp`
   */
  async load(glbUrl, panelUrl, cracksUrl) {
    const loader = new THREE.TextureLoader();
    const [gltf, panel, cracks] = await Promise.all([
      new GLTFLoader().loadAsync(glbUrl),
      loader.loadAsync(panelUrl),
      cracksUrl ? loader.loadAsync(cracksUrl).catch(() => null) : null,
    ]);
    if (this._disposed) return;

    panel.colorSpace = THREE.SRGBColorSpace;
    panel.flipY = false; // glTF UVs already run top-down
    panel.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    this.panel = panel;

    // The GLB ships whichever set it was exported from; swap in this one.
    // Only the printed face takes the panel. The seam material is the bare
    // foil the pack is welded from and has nothing printed on it.
    gltf.scene.traverse((node) => {
      if (!node.isMesh) return;

      // **Tangents, or the normal map bands.**
      //
      // Blender exported them for one mesh out of twenty-three. Without a
      // TANGENT attribute three.js builds the tangent frame from screen-space
      // derivatives, which is constant across a triangle. So a normal map on
      // a coarse mesh shades per-face, and the pack picks up horizontal banding
      // at exactly the frequency of its own topology (76 rows). Computing them
      // here is cheap and fixes it at the source.
      const geometry = node.geometry;
      if (geometry.attributes.uv && geometry.attributes.normal && geometry.index
          && !geometry.attributes.tangent) {
        geometry.computeTangents();
      }

      const material = node.material;
      if (!material) return;
      if (material.name && material.name.includes('crimp')) {
        // The seam and the cut faces are the inside of a foil pouch, not
        // polished steel: at export metalness they mirror the environment and
        // the debris reads as chrome shards rather than pieces of the pack.
        material.metalness = 0.25;
        material.roughness = 0.55;
      } else {
        material.map = panel;
        if (cracks) {
          // Emissive rather than a second colour layer: the mask is black
          // except along the split lines, so only those lines light up, and
          // pushing the intensity past 1 puts them over the bloom threshold.
          // which is what turns a lit line into a ray coming out of the pack.
          cracks.flipY = false;
          cracks.colorSpace = THREE.SRGBColorSpace;
          material.emissiveMap = cracks;
          material.emissive = new THREE.Color(0xffd9a0);
          material.emissiveIntensity = 0;
          this.crackMaterials.push(material);
        }
      }
      material.needsUpdate = true;
    });

    // Group by object, not by mesh. Every piece here carries two materials.
    // the print and the cut face. And a glTF mesh with two primitives arrives
    // as a Group of two Meshes. Traversing for `isMesh` therefore finds each
    // chunk twice and, worse, finds each half separately: hiding the pack would
    // hide only one of its two primitives, and throwing a chunk would throw its
    // front away from its own interior.
    for (const node of gltf.scene.children) {
      if (node.name.startsWith('chunk_')) this.chunks.push(node);
      else if (this.intact === null) this.intact = node;
    }

    // Each chunk's own centre decides where it goes: outward from the middle of
    // the pack, with gravity and a tumble. Deterministic, so a pack always
    // breaks the same way.
    const box = new THREE.Box3();
    this.chunkPlan = this.chunks.map((chunk, i) => {
      // Box3 over the object, because a chunk is a group and has no geometry
      // of its own.
      const centre = box.setFromObject(chunk).getCenter(new THREE.Vector3());
      const dir = centre.clone().sub(chunk.position);
      if (dir.lengthSq() < 1e-9) dir.set(hash(i) - 0.5, hash(i * 3) - 0.5, hash(i * 7) - 0.5);
      dir.normalize();
      return {
        chunk,
        home: chunk.position.clone(),
        // A deliberately wide spread, not a uniformly hard throw. The frame is
        // ~0.24m tall at this camera, so the fastest pieces cross it in a fifth
        // of a second and give the burst its width, while the slowest take over
        // a second and are still tumbling down behind the cards. One speed for
        // all of them buys one or the other, never both.
        // Squared, so the spread is biased toward slow. A uniform spread put
        // every piece out of frame inside a second. Measured: 16 of 22 still
        // visible at +0.15s, none at +1.2s. Squaring leaves roughly a third
        // fast enough to cross the view and give the burst its width, and the
        // rest drifting slowly enough to still be falling while the player is
        // reading their cards.
        velocity: dir.multiplyScalar(0.06 + hash(i * 5.3) ** 2 * 1.15),
        spin: new THREE.Vector3(
          (hash(i * 3.3) - 0.5) * 22,
          (hash(i * 6.6) - 0.5) * 22,
          (hash(i * 9.9) - 0.5) * 17,
        ),
        // Very gentle. Real gravity drops a piece a metre in half a second,
        // which at this scale is four frame-heights; the debris would be gone
        // before it read as falling at all. This drops a lingering piece about
        // a fifth of the frame over two seconds, which is a visible fall.
        gravity: 0.04 + hash(i * 11.3) * 0.10,
      };
    });

    for (const chunk of this.chunks) chunk.visible = false;
    this.pack.add(gltf.scene);
    this.root = gltf.scene;
    this.ready = true;
  }

  /** Where the cursor is, as -1…1 across the canvas. */
  setLean(x, y) {
    this.leanTarget.x = Math.max(-1, Math.min(1, x));
    this.leanTarget.y = Math.max(-1, Math.min(1, y));
  }

  /** Begin the charge. Returns whether this call started it. */
  charge() {
    if (!this.ready || this.phase !== 'idle') return false;
    this.phase = 'charge';
    this.phaseT = 0;
    return true;
  }

  setSize(width, height) {
    this.renderer.setPixelRatio(Math.min(MAX_PIXEL_RATIO, getEffectivePixelRatio()));
    this.renderer.setSize(width, height, false);
    this.composer.setSize(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  /**
   * One frame. Returns 'burst' on the frame the pack goes off and 'done' when
   * the whole sequence is spent, so the caller can fire sound and hand over.
   */
  render(dt, reducedMotion = false) {
    this.time += dt;
    let event = null;

    if (!this.ready) {
      this.composer.render(dt);
      return null;
    }

    // Exponential approach. Frame-rate independent, unlike a fixed fraction.
    const k = reducedMotion ? 1 : 1 - Math.exp(-dt / LEAN_TAU);
    this.lean.x += (this.leanTarget.x - this.lean.x) * k;
    this.lean.y += (this.leanTarget.y - this.lean.y) * k;

    const idle = this.phase === 'idle';
    const drift = reducedMotion || !idle ? 0 : Math.sin(this.time * 0.5) * 0.07;
    this.pack.rotation.y = this.lean.x * MAX_YAW + drift;
    this.pack.rotation.x = this.lean.y * MAX_PITCH;

    if (this.phase === 'charge') {
      this.phaseT += dt;
      const t = Math.min(1, this.phaseT / CHARGE_S);
      // Rises, compresses, and lights from within.
      this.pack.position.y = LIFT * (t * t * (3 - 2 * t));
      this.pack.scale.set(1, 1 - 0.035 * t, 1);
      // Trembles harder the closer the seal is to giving.
      const shake = t ** 3 * 0.055;
      this.pack.rotation.z = Math.sin(this.time * 78) * shake;
      // Ramps late: it should be a glow building under the print, not a lamp
      // switched on over it. `t**2` keeps it out of the way until the seal is
      // nearly gone.

      // The splits open late and fast, so the pack looks intact until it
      // suddenly does not.
      for (const m of this.crackMaterials) m.emissiveIntensity = t ** 3 * 9.0;
      this.coreLight.intensity = t * t * 0.7;

      if (this.phaseT >= CHARGE_S) {
        this.phase = 'burst';
        this.phaseT = 0;
        this.intact.visible = false;
        for (const chunk of this.chunks) chunk.visible = true;
        // Flare rather than cut: the seams are the light, so killing them on
        // the frame the pack breaks takes the burst's only source with it.
        this._crackFlare = 26.0;
        this.pack.rotation.z = 0;
        this.pack.scale.set(1, 1, 1);
        event = 'burst';
      }
    } else if (this.phase === 'burst') {
      this.phaseT += dt;
      const t = this.phaseT;

      this._crackFlare = Math.max(0, (this._crackFlare || 0) - dt * 55);
      for (const m of this.crackMaterials) m.emissiveIntensity = this._crackFlare;

      for (const plan of this.chunkPlan) {
        const { chunk, home, velocity, spin, gravity } = plan;
        chunk.position.set(
          home.x + velocity.x * t,
          home.y + velocity.y * t - 0.5 * gravity * t * t,
          home.z + velocity.z * t,
        );
        chunk.rotation.x += spin.x * dt;
        chunk.rotation.y += spin.y * dt;
        chunk.rotation.z += spin.z * dt;
      }

      // The core blows out and fades; the bloom does the rest.
      // Three beats, not two: the flash, then the throw, then a dissipation
      // the effect can end on. Cutting straight from peak to nothing is what
      // makes a burst feel like a switch rather than an event.
      // Kept smaller than the pack it came out of. At 0.15m against a 0.11m
      // pack this was a lamp wider than the object, additive and bloomed, and
      // the frame simply saturated to white. The core is a source; the bloom
      // and the sparks are what make it look big.
      const flash = Math.min(1, t / 0.12);
      const fade = Math.max(0, 1 - Math.max(0, t - 0.12) / 0.62);

      this.coreLight.intensity = Math.max(0, 1.9 * (1 - t / 0.4));

      // Reported once; the physics does not stop with it. Freezing the chunks
      // at BURST_S left them hanging wherever they happened to be, which is
      // the opposite of debris. It should still be falling out of frame while
      // the player is reading their cards.
      if (t >= BURST_S && !this._doneFired) {
        this._doneFired = true;
        event = 'done';
      }
    }

    // **The composer only runs while there is something to bloom.**
    //
    // Once the seams have gone there is nothing emissive left. Just lit
    // chunks tumbling. And an HDR target plus a five-level bloom chain plus
    // SMAA every frame buys nothing. It costs plenty though, and it was being
    // paid across the whole reveal, competing with fifteen card springs for
    // the same frame. That is the stutter.
    if (this.phase === 'charge' || (this.phase === 'burst' && this.phaseT < BLOOM_UNTIL_S)) {
      this.composer.render(dt);
    } else {
      this.renderer.render(this.scene, this.camera);
    }
    return event;
  }

  dispose() {
    this._disposed = true;
    this.composer.dispose();
    this.panel?.dispose();
    this.root?.traverse((node) => {
      if (!node.isMesh) return;
      node.geometry.dispose();
      const materials = Array.isArray(node.material) ? node.material : [node.material];
      for (const m of materials) m?.dispose();
    });
    this.envRT.dispose();
    this.pmrem.dispose();
    this.renderer.dispose();
  }
}
